/**
 * Type guard to check if a promise is cancelable.
 * Returns true if the promise has a cancel method.
 * @util
 *
 * @template T - The type of value the promise resolves to
 * @param promise - The promise to check
 * @returns True if the promise is cancelable (has a cancel method), false otherwise
 *
 * @example
 * ```typescript
 * const promise = makePromiseCancelable(Promise.resolve(), () => {});
 * if (isCancelablePromise(promise)) {
 *     promise.cancel(); // TypeScript knows this is safe
 * }
 * ```
 */
export function isCancelablePromise(promise) {
    return 'cancel' in promise;
}
/**
 * Makes a promise cancelable by adding a cancel method.
 * When canceled, the cancel callback is called.
 * The original promise remains unchanged.
 * @util
 *
 * @template T - The type of value the promise resolves to
 * @param promise - The promise to make cancelable
 * @param cancelCallback - Function to call when the promise is canceled
 * @returns A cancelable version of the promise that includes a cancel method
 *
 * @example
 * ```typescript
 * const promise = new Promise(resolve => {
 *     const timeout = setTimeout(resolve, 1000);
 *     return () => clearTimeout(timeout);
 * });
 *
 * const cancelable = makePromiseCancelable(promise, () => {
 *     console.log('Canceled');
 * });
 *
 * cancelable.cancel(); // Logs "Canceled"
 * ```
 */
export function makePromiseCancelable(promise, cancelCallback) {
    const cancelable = promise;
    cancelable.cancel = cancelCallback;
    return cancelable;
}
//# sourceMappingURL=Cancelable.js.map